/* eslint-disable no-restricted-globals */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import moment from 'moment';
import { find } from 'lodash';

import DateRangePickerModal from './DateRangePickerModal';

import {
  diff,
  getDatesFromRange,
  getDaysInPast,
  getRangeFromDates,
  getRangeFromDays,
  isToday
} from '../../../utils/dates';

import {
  futureDateRange as FUTURE_DATE_RANGE_FILTER_OPTIONS,
  dateRange as DATE_RANGE_FILTER_OPTIONS,
  DATE_RANGE_CUSTOM,
  MIN_DATE
} from '../../../config/filterOptions';

export const DATE_RANGE_FILTER_ID = 'dateRangeFilter';

const findRangeValue = (range, options) =>
  find(options, opt => opt.value === range);

const getPredefinedRangeValue = (startDate, endDate) => {
  const range = diff(startDate, endDate);
  return options => findRangeValue(range, options);
};

const customRangeValue = { value: DATE_RANGE_CUSTOM, label: 'Custom ' };

const getRangeValue = ({ date, rangeOptions, findPredefinedRangeValue }) => {
  let rangeValue;
  if (isToday(date)) {
    rangeValue = findPredefinedRangeValue(rangeOptions);
  }
  return rangeValue;
};

class DateRangeFilter extends React.Component {
  static getDerivedStateFromProps({ value, isFuture }) {
    let rangeValue;
    const selectedValue = value && (value.aliasValue || value.value);
    if (selectedValue) {
      const [startDate, endDate] = getDatesFromRange(selectedValue);
      const findPredefinedRangeValue = getPredefinedRangeValue(
        startDate,
        endDate
      );
      if (isFuture) {
        rangeValue = getRangeValue({
          date: startDate,
          rangeOptions: FUTURE_DATE_RANGE_FILTER_OPTIONS,
          findPredefinedRangeValue
        });
      } else {
        rangeValue = getRangeValue({
          date: endDate,
          rangeOptions: DATE_RANGE_FILTER_OPTIONS,
          findPredefinedRangeValue
        });
      }
      if (!rangeValue) {
        rangeValue = customRangeValue;
      }
    }
    return { rangeValue };
  }

  constructor(props) {
    super(props);

    this.state = {
      customSelected: false,
      rangeValue: undefined
    };

    this.onChange = this.onChange.bind(this);
    this.onCustomRangeSelected = this.onCustomRangeSelected.bind(this);
    this.onRangePickerClosed = this.onRangePickerClosed.bind(this);
  }

  onCustomRangeSelected(dates) {
    const { onChange } = this.props;

    // The date range must comply with the following:
    //   1. Include only completed days.
    //   2. The end date is not inclusive.
    //
    // If the end date is today, we can use it, as it'll fit the requirements.
    // If it isn't, it means it's a day in the past, and we need to include it
    // in the range. We do this by adding 1 day.
    const realEndDate = isToday(dates.endDate)
      ? dates.endDate
      : moment(dates.endDate).add(1, 'day');

    const range = getRangeFromDates({
      startDate: dates.startDate,
      endDate: realEndDate
    });

    const dayBeforeEndDate = moment(realEndDate).subtract(1, 'day');
    range.label = getRangeFromDates({
      startDate: dates.startDate,
      endDate: dayBeforeEndDate
    }).label;

    this.setState({ customSelected: false }, () => onChange(range));
  }

  onPredefinedRangeSelected({ value: daysBack }) {
    const { onChange, isFuture, isAliasMode } = this.props;
    const selectedRange = getRangeFromDays(daysBack, isFuture);
    let rangeValue;

    if (isAliasMode) {
      rangeValue = getDaysInPast(daysBack);
    }
    if (rangeValue) {
      rangeValue.aliasValue = selectedRange.value;
    } else {
      rangeValue = selectedRange;
    }
    onChange(rangeValue);
  }

  onRangePickerClosed() {
    this.setState({ customSelected: false });
  }

  onChange(selectedOption) {
    const { onChange } = this.props;

    if (!selectedOption || selectedOption.value === null) {
      onChange(null);
    } else if (selectedOption.value === DATE_RANGE_CUSTOM) {
      this.setState({ customSelected: true });
    } else {
      this.onPredefinedRangeSelected(selectedOption);
    }
  }

  render() {
    let minDate,
      maxDate = moment().add(2, 'y'),
      validDates = [];
    const { dates = [], isValidDatesMode } = this.props;
    if (isValidDatesMode && dates && dates.length > 0) {
      validDates = dates;
      minDate = validDates[0];
    } else {
      minDate = this.props.dataRectriction
        ? moment().subtract(this.props.dataRectriction, 'months')
        : MIN_DATE;
    }
    return (
      <Fragment>
        <Select
          {...this.props}
          onChange={this.onChange}
          value={this.state.rangeValue}
        />
        <DateRangePickerModal
          props={this.props}
          onClose={this.onRangePickerClosed}
          onDatesChange={this.onCustomRangeSelected}
          minDate={minDate}
          maxDate={maxDate}
          show={this.state.customSelected}
          dates={validDates}
        />
      </Fragment>
    );
  }
}

DateRangeFilter.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dates: PropTypes.arrayOf(PropTypes.shape({})),
  isFuture: PropTypes.bool,
  isAliasMode: PropTypes.bool,
  isValidDatesMode: PropTypes.bool
};

DateRangeFilter.defaultProps = {
  value: null,
  isFuture: false,
  isAliasMode: false,
  isValidDatesMode: true,
  dates: []
};

export default DateRangeFilter;
